import type { ShopOptionType, ShopOptionValue, ShopSize } from "../../../api/shop";
import { ONE_SIZE } from "../shopFormat";

/** The server's limit: at most this many SKUs per product. */
export const MAX_SKUS = 200;
export const TOO_MANY_COMBINATIONS = "Too many combinations — remove some values or sizes";

/** A style's identity: its option value ids, sorted ("" for a product with no values). The server matches on it. */
export const styleKey = (optionValueIds: readonly string[]) => [...optionValueIds].sort().join("+");
/** A SKU's identity: its style and its size ("|" for a simple product). */
export const skuKey = (style: string, sizeId: string | null) => `${style}|${sizeId ?? ""}`;

export type StylePreview = { key: string; optionValueIds: string[]; values: ShopOptionValue[]; label: string };
export type SkuPreview = {
  key: string;
  styleKey: string;
  optionValueIds: string[];
  sizeId: string | null;
  size: ShopSize | null;
  values: ShopOptionValue[];
  label: string;
};
export type Combinations = {
  /** The picked values and sizes that exist, in catalog order */
  optionValueIds: string[];
  sizeIds: string[];
  styles: StylePreview[];
  skus: SkuPreview[];
  /** How many SKUs the picks make (also when there are too many to list) */
  count: number;
  tooMany: boolean;
};
export type CatalogShape = { optionTypes: ShopOptionType[]; sizes: ShopSize[] };

const byOrder = <T extends { displayOrder: number }>(list: readonly T[]) => [...list].sort((a, b) => a.displayOrder - b.displayOrder);

/** A SKU's label: value labels in option-type order, then the size, joined with " · "; nothing → "One size". */
export const skuLabel = (values: readonly ShopOptionValue[], size: ShopSize | null) => {
  const parts = [...values.map((v) => v.label), ...(size ? [size.label] : [])];
  return parts.length > 0 ? parts.join(" · ") : ONE_SIZE;
};

/**
 * Every style (one value of each option type that has a pick) and every SKU (style × picked size), in catalog
 * order: option-type order, value order, then size order. The server's rule. No values: one style; no sizes: one
 * SKU per style. Past MAX_SKUS nothing is listed.
 */
export function expandCombinations(catalog: CatalogShape, optionValueIds: readonly string[], sizeIds: readonly string[]): Combinations {
  const pickedValues = new Set(optionValueIds);
  const groups = byOrder(catalog.optionTypes)
    .map((type) => byOrder(type.values).filter((value) => pickedValues.has(value.id)))
    .filter((values) => values.length > 0);
  const pickedSizes = new Set(sizeIds);
  const sizes = byOrder(catalog.sizes).filter((size) => pickedSizes.has(size.id));
  const count = groups.reduce((n, values) => n * values.length, 1) * Math.max(1, sizes.length);
  const base = { optionValueIds: groups.flatMap((values) => values.map((v) => v.id)), sizeIds: sizes.map((s) => s.id), count };
  if (count > MAX_SKUS) return { ...base, styles: [], skus: [], tooMany: true };

  let combos: ShopOptionValue[][] = [[]];
  for (const values of groups) combos = combos.flatMap((combo) => values.map((value) => [...combo, value]));
  const styles: StylePreview[] = combos.map((values) => {
    const ids = values.map((v) => v.id);
    return { key: styleKey(ids), optionValueIds: ids, values, label: values.map((v) => v.label).join(" · ") };
  });
  const slots: (ShopSize | null)[] = sizes.length > 0 ? sizes : [null];
  const skus: SkuPreview[] = styles.flatMap((style) =>
    slots.map((size) => ({
      key: skuKey(style.key, size?.id ?? null),
      styleKey: style.key,
      optionValueIds: style.optionValueIds,
      sizeId: size?.id ?? null,
      size,
      values: style.values,
      label: skuLabel(style.values, size),
    }))
  );
  return { ...base, styles, skus, tooMany: false };
}

/** The SKU the server generates for a blank SKU: product code, value codes, size code, joined with "-". */
export const previewSku = (productCode: string, values: readonly ShopOptionValue[], size: ShopSize | null) =>
  productCode ? [productCode, ...values.map((v) => v.code), ...(size ? [size.code] : [])].join("-") : "";
